import { useState, type ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { useAuth } from "../lib/auth";
import type { Api } from "../lib/api";
import type { Me } from "../lib/types";
import { Brand } from "./Brand";
import { ChatWidget } from "./ChatWidget";

/**
 * Portal shell. Primary customer surfaces:
 *  - Builds (Move/Accelerate) — signed release downloads.
 *  - Log uploads (Understand) — logs into the account's own isolated bucket.
 */
const navItems = [
  { to: "/app/builds", label: "Builds", sub: "Downloads" },
  { to: "/app/documents", label: "Documents", sub: "Library" },
  { to: "/app/licenses", label: "Licenses", sub: "Entitlements" },
  { to: "/app/logs", label: "Log uploads", sub: "Support logs" },
  { to: "/app/tickets", label: "Tickets", sub: "Support" },
  { to: "/app/faq", label: "FAQ", sub: "Help" },
];

const adminNavItems = [
  { to: "/app/directory", label: "Accounts", sub: "Directory" },
  { to: "/app/support", label: "Support", sub: "Queue" },
  { to: "/app/admin", label: "Admin", sub: "Settings" },
];

export function Layout({ me, api, children }: { me: Me; api: Api; children: ReactNode }) {
  const { signOut } = useAuth();
  const isAdmin = me.role === "platform_admin";
  const [menuOpen, setMenuOpen] = useState(false);

  const items = isAdmin ? [...navItems, ...adminNavItems] : navItems;

  return (
    <div className="min-h-full">
      <header className="sticky top-0 z-20 border-b border-line bg-bg/80 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-shell items-center justify-between px-6">
          <Brand />
          <nav className="hidden items-center gap-1 md:flex">
            {items.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  `rounded-pill px-4 py-2 text-sm font-medium transition ${
                    isActive ? "bg-accent/12 text-accent-strong" : "text-ink-muted hover:text-ink"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <div className="hidden text-right sm:block">
              <div className="text-sm text-ink">{me.email}</div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-ink-dim">
                {me.account.name} · {me.role.replace("_", " ")}
              </div>
            </div>
            <button className="hidden btn-ghost md:inline-flex" onClick={() => void signOut()}>
              Sign out
            </button>
            <button
              type="button"
              className="btn-ghost !px-3 md:hidden"
              aria-label="Toggle navigation menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((o) => !o)}
            >
              {menuOpen ? "✕" : "☰"}
            </button>
          </div>
        </div>
        {menuOpen && (
          <nav className="border-t border-line bg-bg md:hidden">
            <div className="mx-auto max-w-shell px-6 py-3">
              <div className="mb-3 border-b border-line/60 pb-3 sm:hidden">
                <div className="text-sm text-ink">{me.email}</div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-ink-dim">
                  {me.account.name} · {me.role.replace("_", " ")}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                {items.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    onClick={() => setMenuOpen(false)}
                    className={({ isActive }) =>
                      `rounded-sm px-4 py-2 text-sm font-medium transition ${
                        isActive
                          ? "bg-accent/12 text-accent-strong"
                          : "text-ink-muted hover:text-ink"
                      }`
                    }
                  >
                    {n.label}
                  </NavLink>
                ))}
                <button
                  className="btn-ghost mt-2 justify-start"
                  onClick={() => {
                    setMenuOpen(false);
                    void signOut();
                  }}
                >
                  Sign out
                </button>
              </div>
            </div>
          </nav>
        )}
      </header>
      <main className="mx-auto max-w-shell px-6 py-10">{children}</main>
      <footer className="mx-auto max-w-shell px-6 py-8 text-xs text-ink-dim">
        PumaMesh Inc. — zero-trust, post-quantum data movement. All access is authenticated,
        authorized, and audited.
      </footer>
      <ChatWidget api={api} />
    </div>
  );
}
